// Pure parse/save model for ContextForm (extracted per the 2026-07 DI UI audit,
// findings F10/F11/F12/F13/F14/F17).
//
// THE RULE: saving a context must never change a key the operator did not edit.
// The old save rebuilt probes/patterns/spawn guidance wholesale from form state,
// destroying every field the form didn't model — including `query_json` (38/43
// production probes; the cached-query optimisation) and `report_detail` (43/43).
//
// Mechanism: every form row carries `_original` (the raw JSON node it came
// from). On save, each modeled field is compared against what the ORIGINAL
// parses to; only genuinely edited fields are written back. Unmodeled fields —
// and `_`-prefixed annotation keys — ride along untouched. The contract test is
// round-trip stability: parse -> save with no edits -> deep-equal, across the
// full live config corpus (contextFormModel.roundtrip.test.js).

const clone = (value) => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)))

const canonEqual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// ---------------------------------------------------------------------------
// Field specs: how a JSON value maps into a form value (toForm) and back
// (fromForm). fromForm returning undefined means "delete the key".
// ---------------------------------------------------------------------------

const specs = {
    text: {
        toForm: (v) => (typeof v === 'string' ? v : ''),
        fromForm: (v) => (v && String(v).trim() !== '' ? v : undefined),
    },
    // Text that must survive as-is even when empty-string is meaningful.
    textKeepEmpty: {
        toForm: (v) => (typeof v === 'string' ? v : ''),
        fromForm: (v) => (typeof v === 'string' ? v : undefined),
    },
    bool: {
        toForm: (v) => (typeof v === 'boolean' ? v : undefined),
        fromForm: (v) => (typeof v === 'boolean' ? v : undefined),
    },
    number: {
        toForm: (v) => (typeof v === 'number' ? v : ''),
        fromForm: (v) => {
            if (v === '' || v === null || v === undefined) return undefined
            const num = Number(v)
            return Number.isNaN(num) ? undefined : num
        },
    },
    csvArray: {
        toForm: (v) => (Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : ''),
        fromForm: (v) => {
            if (Array.isArray(v)) return v.length > 0 ? v : undefined
            if (typeof v !== 'string' || v.trim() === '') return undefined
            return v
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
        },
    },
    jsonText: {
        toForm: (v) => {
            if (v === undefined || v === null) return ''
            if (typeof v === 'object') return JSON.stringify(v, null, 2)
            return String(v)
        },
        fromForm: (v) => {
            if (v === undefined || v === null || String(v).trim() === '') return undefined
            try {
                return JSON.parse(v)
            } catch {
                return v
            }
        },
    },
}

/**
 * Merge modeled fields into a copy of `original`. A field is written only when
 * the form value differs from what the original value parses to — untouched
 * fields keep the original representation, including absence.
 */
const mergeRow = (original, formRow, fieldSpecs) => {
    const out = original ? clone(original) : {}
    for (const [key, spec] of Object.entries(fieldSpecs)) {
        const originalAsForm = spec.toForm(original ? original[key] : undefined)
        const formValue = formRow[key]
        if (canonEqual(formValue, originalAsForm)) continue
        const next = spec.fromForm(formValue)
        if (next === undefined) delete out[key]
        else out[key] = next
    }
    return out
}

// ---------------------------------------------------------------------------
// Probe families. `classification` is the engine's Chat.java ROUTING key
// (SCHEMA.md v2.9.21: DATASET | ML_GROUP | ML_PERIOD | ML_RELATIONSHIP |
// ML_CLUSTER) and is orthogonal to `type` (execution mode). The old form
// conflated them and wrote invalid values (NATURAL_LANGUAGE / ZERO_OVERHEAD /
// ML_COMPARISON) — audit F11.
// ---------------------------------------------------------------------------

export const VALID_CLASSIFICATIONS = [
    'DATASET',
    'ML_GROUP',
    'ML_PERIOD',
    'ML_RELATIONSHIP',
    'ML_CLUSTER',
]

const NL_PROBE_FIELDS = {
    probe_id: specs.text,
    name: specs.text,
    classification: specs.text,
    prompt: specs.text,
    purpose: specs.text,
    output_columns: specs.csvArray,
    expected_output_type: specs.text,
    dataset_requirements: specs.jsonText,
}

const ZO_PROBE_FIELDS = {
    probe_id: specs.text,
    name: specs.text,
    classification: specs.text,
    drill_dimension: specs.text,
    metrics: specs.csvArray,
    inherit_parent_filter: specs.bool,
    sort_by: specs.text,
    purpose: specs.text,
    output_columns: specs.csvArray,
    expected_output_type: specs.text,
    dataset_requirements: specs.jsonText,
}

const ML_PROBE_FIELDS = {
    probe_id: specs.text,
    name: specs.text,
    classification: specs.text,
    ml_attributes: specs.csvArray,
    comparison_baseline: specs.text,
    purpose: specs.text,
    output_columns: specs.csvArray,
    expected_output_type: specs.text,
    dataset_requirements: specs.jsonText,
}

const PATTERN_FIELDS = {
    description: specs.text,
    detection_expression: specs.text,
    confidence_threshold: specs.number,
    action: specs.text,
    affected_metric: specs.text,
    // Real loader fields (HierarchicalDomainIntelligenceLoader:1496-1505) even
    // though no current production pattern sets them.
    capture_triggers: specs.bool,
    trigger_threshold: specs.number,
    trigger_expression: specs.text,
}

const INTERPRETATION_FIELDS = {
    severity_thresholds: specs.jsonText,
    business_impact: specs.text,
    recommended_action: specs.text,
    executive_framing: specs.text,
}

const SPAWN_FIELDS = {
    trigger_pattern: specs.text,
    spawn_mode: specs.text,
    spawn_to_context: specs.text,
    entity_dimension: specs.text,
    spawn_strategy: specs.text,
    max_spawns: specs.number,
    priority_modifier: specs.number,
    reason: specs.text,
}

const TERMINATION_FIELDS = {
    condition: specs.text,
    action: specs.text,
    message: specs.text,
    severity: specs.text,
}

const DEFINITION_FIELDS = {
    description: specs.text,
    formula: specs.text,
    format: specs.text,
    type: specs.text,
    dataset: specs.text,
    dataset_id: specs.text,
    column_name: specs.text,
    thresholds: specs.jsonText,
}

const METADATA_FIELDS = {
    display_name: specs.text,
    purpose: specs.text,
    description: specs.text,
    key_metrics: specs.csvArray,
    usage: specs.text,
    multi_dataset_note: specs.text,
    last_validated: specs.text,
}

const SCREENING_RULE_FIELDS = {
    primary_indicator: specs.text,
    severity_filter: specs.text,
    critical_definition: specs.text,
    sort_order: specs.text,
}

const CLIENT_FEEDBACK_FIELDS = {
    source: specs.text,
    summary: specs.text,
    recommended_stores: specs.csvArray,
    reasoning: specs.text,
}

const VALIDATION_RESULT_FIELDS = {
    previous_approach: specs.jsonText,
    corrected_approach: specs.jsonText,
}

const BUSINESS_TEXT_FIELDS = {
    methodology: specs.text,
    owner: specs.text,
    frequency: specs.text,
    escalation: specs.text,
}

const KPI_FIELDS = {
    goal: specs.text,
    acceptable: specs.text,
    action_required: specs.text,
}

const FISCAL_FIELDS = {
    Q1: specs.text,
    Q2: specs.text,
    Q3: specs.text,
    Q4: specs.text,
    note: specs.text,
}

// Real synthesis config lives at investigation.synthesis_options (audit F12 —
// the old form read/wrote a phantom top-level `synthesis` that nothing uses).
const SYNTHESIS_OPTION_FIELDS = {
    ml_enabled: specs.bool,
    ml_query: specs.text,
    ml_classification: specs.text,
    ml_threshold: specs.number,
    ml_timeout: specs.number,
    max_queries: specs.number,
    narrative_voice: specs.text,
    internal_guidance: {
        toForm: (v) => (Array.isArray(v) ? v.join('\n') : typeof v === 'string' ? v : ''),
        fromForm: (v) => {
            if (typeof v !== 'string' || v.trim() === '') return undefined
            const lines = v
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean)
            return lines.length > 0 ? lines : undefined
        },
    },
}

const rowFromSpecs = (original, fieldSpecs) => {
    const row = { _original: original ? clone(original) : null }
    for (const [key, spec] of Object.entries(fieldSpecs)) {
        row[key] = spec.toForm(original ? original[key] : undefined)
    }
    return row
}

// Probe family detection (unchanged behaviour: markers, not classification).
const isZoProbe = (p) => p.type === 'zero_overhead_drill' || p.drill_dimension !== undefined
const isMlProbe = (p) => p.type === 'ml_period_comparison' || p.ml_attributes !== undefined

// Empty templates for new rows. Classifications are VALID engine values.
export const EMPTY_NL_PROBE = () => ({
    ...rowFromSpecs(null, NL_PROBE_FIELDS),
    classification: 'DATASET',
    expected_output_type: 'table',
})
export const EMPTY_ZO_PROBE = () => ({
    ...rowFromSpecs(null, ZO_PROBE_FIELDS),
    classification: 'DATASET',
    inherit_parent_filter: true,
    expected_output_type: 'table',
    _newType: 'zero_overhead_drill',
})
export const EMPTY_ML_PROBE = () => ({
    ...rowFromSpecs(null, ML_PROBE_FIELDS),
    classification: 'ML_PERIOD',
    comparison_baseline: 'same_month_last_year',
    expected_output_type: 'table',
})
export const EMPTY_PATTERN = () => ({
    ...rowFromSpecs(null, PATTERN_FIELDS),
    name: '',
    action: 'investigate',
    confidence_threshold: 0.5,
})
export const EMPTY_INTERPRETATION = () => ({
    ...rowFromSpecs(null, INTERPRETATION_FIELDS),
    pattern_name: '',
})
export const EMPTY_SPAWN = () => ({
    ...rowFromSpecs(null, SPAWN_FIELDS),
    spawn_mode: 'investigate',
    spawn_strategy: 'worst_first',
    max_spawns: 3,
    priority_modifier: 1,
})
export const EMPTY_TERMINATION = () => ({
    ...rowFromSpecs(null, TERMINATION_FIELDS),
    action: 'complete_with_success',
    severity: 'info',
})

// ---------------------------------------------------------------------------
// Parse: JSON -> form state
// ---------------------------------------------------------------------------

const parseApplicableDatasets = (datasets) => {
    if (!datasets) return []
    if (Array.isArray(datasets)) return [{ category: 'default', datasets: clone(datasets) }]
    if (typeof datasets === 'object') {
        return Object.entries(datasets).map(([category, list]) => ({
            category,
            datasets: Array.isArray(list)
                ? clone(list)
                : String(list)
                      .split(',')
                      .map((d) => d.trim())
                      .filter(Boolean),
        }))
    }
    return []
}

export const parseContextToForm = (data, contextName) => {
    const investigation = data.investigation || {}
    const rawProbes = investigation.probes || data.probes
    const probes = Array.isArray(rawProbes) ? rawProbes : []

    const nlProbes = []
    const zoProbes = []
    const mlProbes = []
    probes.forEach((probe, index) => {
        if (isZoProbe(probe)) {
            zoProbes.push({ ...rowFromSpecs(probe, ZO_PROBE_FIELDS), _originalIndex: index })
        } else if (isMlProbe(probe)) {
            mlProbes.push({ ...rowFromSpecs(probe, ML_PROBE_FIELDS), _originalIndex: index })
        } else {
            nlProbes.push({ ...rowFromSpecs(probe, NL_PROBE_FIELDS), _originalIndex: index })
        }
    })

    const patternsObj = investigation.patterns || data.patterns || {}
    const patterns = Object.entries(patternsObj).map(([name, pattern]) => ({
        ...rowFromSpecs(pattern, PATTERN_FIELDS),
        name,
        // Back-compat: the UI's field is `expression`; production key is
        // detection_expression.
        expression: specs.text.toForm(pattern?.detection_expression ?? pattern?.expression),
    }))

    const guidanceObj = investigation.interpretation_guidance || data.interpretation_guidance || {}
    const interpretationGuidance = Object.entries(guidanceObj).map(([patternName, content]) => ({
        ...rowFromSpecs(content, INTERPRETATION_FIELDS),
        pattern_name: patternName,
    }))

    const spawnData = investigation.spawn_guidance || data.spawn_guidance || []
    const spawnGuidance = (Array.isArray(spawnData) ? spawnData : []).map((s) => ({
        ...rowFromSpecs(s, SPAWN_FIELDS),
    }))

    const termData = investigation.termination_conditions || data.termination_conditions || []
    const terminationConditions = (Array.isArray(termData) ? termData : []).map((t) => ({
        ...rowFromSpecs(t, TERMINATION_FIELDS),
    }))

    const defsObj = data.definitions || data.local_definitions || {}
    const localDefinitions = Object.entries(defsObj).map(([name, def]) => ({
        ...rowFromSpecs(def, DEFINITION_FIELDS),
        name,
    }))

    const metadata = data.metadata || {}
    const synthesisOptions = investigation.synthesis_options || {}

    return {
        name: data.name || contextName || '',
        metadata: rowFromSpecs(metadata, METADATA_FIELDS),
        applicable_datasets: parseApplicableDatasets(metadata.applicable_datasets),
        screening_rules: rowFromSpecs(metadata.screening_rules, SCREENING_RULE_FIELDS),
        client_feedback: rowFromSpecs(metadata.client_feedback, CLIENT_FEEDBACK_FIELDS),
        validation_results: rowFromSpecs(metadata.validation_results, VALIDATION_RESULT_FIELDS),
        local_definitions: localDefinitions,
        nl_probes: nlProbes,
        zo_probes: zoProbes,
        ml_probes: mlProbes,
        patterns,
        interpretation_guidance: interpretationGuidance,
        spawn_guidance: spawnGuidance,
        termination_conditions: terminationConditions,
        business_context: {
            ...rowFromSpecs(data.business_context, BUSINESS_TEXT_FIELDS),
            kpi_targets: rowFromSpecs(data.business_context?.kpi_targets, KPI_FIELDS),
            fiscal_calendar: rowFromSpecs(data.business_context?.fiscal_calendar, FISCAL_FIELDS),
        },
        // Production keeps spawn_enabled inside `investigation` (top-level read
        // is legacy back-compat).
        spawn_enabled: investigation.spawn_enabled ?? data.spawn_enabled ?? true,
        synthesis_options: rowFromSpecs(synthesisOptions, SYNTHESIS_OPTION_FIELDS),
    }
}

// ---------------------------------------------------------------------------
// Save: form state -> JSON (merge, never rebuild)
// ---------------------------------------------------------------------------

const mergeProbeRow = (row) => {
    const fieldSpecs = row._newType === 'zero_overhead_drill' || isZoOriginal(row)
        ? ZO_PROBE_FIELDS
        : isMlOriginal(row)
          ? ML_PROBE_FIELDS
          : NL_PROBE_FIELDS
    const merged = mergeRow(row._original, row, fieldSpecs)
    if (!row._original && row._newType) merged.type = row._newType
    return merged
}

const isZoOriginal = (row) =>
    (row._original && isZoProbe(row._original)) || row.drill_dimension !== undefined
const isMlOriginal = (row) =>
    (row._original && isMlProbe(row._original)) || row.ml_attributes !== undefined

const buildProbes = (formData) => {
    const rows = [...formData.nl_probes, ...formData.zo_probes, ...formData.ml_probes].filter(
        (row) => row.probe_id
    )
    rows.sort((a, b) => {
        const ai = a._originalIndex ?? Number.MAX_SAFE_INTEGER
        const bi = b._originalIndex ?? Number.MAX_SAFE_INTEGER
        return ai - bi
    })
    return rows.map(mergeProbeRow)
}

const buildKeyedObject = (rows, keyField, buildValue) => {
    const out = {}
    for (const row of rows) {
        const key = row[keyField]
        if (!key) continue
        out[key] = buildValue(row)
    }
    return out
}

const buildPatterns = (formData) =>
    buildKeyedObject(formData.patterns, 'name', (row) =>
        mergeRow(row._original, { ...row, detection_expression: row.expression }, PATTERN_FIELDS)
    )

const buildInterpretationGuidance = (formData) =>
    buildKeyedObject(formData.interpretation_guidance, 'pattern_name', (row) =>
        mergeRow(row._original, row, INTERPRETATION_FIELDS)
    )

const buildDefinitions = (formData) =>
    buildKeyedObject(formData.local_definitions, 'name', (row) =>
        mergeRow(row._original, row, DEFINITION_FIELDS)
    )

const buildApplicableDatasets = (formData) => {
    const out = {}
    for (const entry of formData.applicable_datasets) {
        if (entry.category && Array.isArray(entry.datasets) && entry.datasets.length > 0) {
            out[entry.category] = clone(entry.datasets)
        }
    }
    return Object.keys(out).length > 0 ? out : undefined
}

/** Merge a keyed sub-object; returns undefined (delete) when merge yields {}. */
const mergeSection = (original, formRow, fieldSpecs) => {
    const merged = mergeRow(original, formRow, fieldSpecs)
    return Object.keys(merged).length > 0 ? merged : undefined
}

const setOrDelete = (target, key, value) => {
    if (value === undefined) delete target[key]
    else target[key] = value
}

export const buildContextFromForm = (data, formData, contextName) => {
    const out = clone(data || {})

    out.name = formData.name || data?.name || contextName

    // metadata --------------------------------------------------------------
    const metadata = mergeRow(data?.metadata, formData.metadata, METADATA_FIELDS)
    const originalDatasets = parseApplicableDatasets(data?.metadata?.applicable_datasets)
    if (!canonEqual(formData.applicable_datasets, originalDatasets)) {
        setOrDelete(metadata, 'applicable_datasets', buildApplicableDatasets(formData))
    }
    setOrDelete(
        metadata,
        'screening_rules',
        mergeSection(data?.metadata?.screening_rules, formData.screening_rules, SCREENING_RULE_FIELDS)
    )
    setOrDelete(
        metadata,
        'client_feedback',
        mergeSection(data?.metadata?.client_feedback, formData.client_feedback, CLIENT_FEEDBACK_FIELDS)
    )
    setOrDelete(
        metadata,
        'validation_results',
        mergeSection(
            data?.metadata?.validation_results,
            formData.validation_results,
            VALIDATION_RESULT_FIELDS
        )
    )
    out.metadata = metadata

    // definitions (write back under the key the file already used) ----------
    const definitions = buildDefinitions(formData)
    if (data?.local_definitions && !data?.definitions) out.local_definitions = definitions
    else out.definitions = definitions

    // business context -------------------------------------------------------
    const businessContext = mergeRow(data?.business_context, formData.business_context, BUSINESS_TEXT_FIELDS)
    setOrDelete(
        businessContext,
        'kpi_targets',
        mergeSection(data?.business_context?.kpi_targets, formData.business_context.kpi_targets, KPI_FIELDS)
    )
    setOrDelete(
        businessContext,
        'fiscal_calendar',
        mergeSection(
            data?.business_context?.fiscal_calendar,
            formData.business_context.fiscal_calendar,
            FISCAL_FIELDS
        )
    )
    if (Object.keys(businessContext).length > 0) out.business_context = businessContext
    else delete out.business_context

    // investigation ----------------------------------------------------------
    const hasInvestigation = Boolean(data?.investigation)
    const target = hasInvestigation ? { ...clone(data.investigation) } : out

    // Only write probes/patterns when the file had them or the form added rows —
    // legacy query_templates contexts must not gain empty keys.
    const probes = buildProbes(formData)
    const hadProbes = Array.isArray(data?.investigation?.probes || data?.probes)
    if (hadProbes || probes.length > 0) target.probes = probes

    const patterns = buildPatterns(formData)
    const hadPatterns = Boolean(data?.investigation?.patterns || data?.patterns)
    if (hadPatterns || Object.keys(patterns).length > 0) target.patterns = patterns
    setOrDelete(
        target,
        'interpretation_guidance',
        Object.keys(buildInterpretationGuidance(formData)).length > 0
            ? buildInterpretationGuidance(formData)
            : undefined
    )
    const spawnRows = formData.spawn_guidance.filter((row) => row.trigger_pattern || row._original)
    setOrDelete(
        target,
        'spawn_guidance',
        spawnRows.length > 0 ? spawnRows.map((row) => mergeRow(row._original, row, SPAWN_FIELDS)) : undefined
    )
    const termRows = formData.termination_conditions.filter((row) => row.condition || row._original)
    setOrDelete(
        target,
        'termination_conditions',
        termRows.length > 0
            ? termRows.map((row) => mergeRow(row._original, row, TERMINATION_FIELDS))
            : undefined
    )

    // spawn_enabled lives in investigation (audit F17); only write on change.
    const originalSpawnEnabled = data?.investigation?.spawn_enabled ?? data?.spawn_enabled ?? true
    if (formData.spawn_enabled !== originalSpawnEnabled) {
        target.spawn_enabled = formData.spawn_enabled
    }

    // synthesis_options is the REAL key (audit F12); the phantom top-level
    // `synthesis` is never written.
    setOrDelete(
        target,
        'synthesis_options',
        mergeSection(
            data?.investigation?.synthesis_options,
            formData.synthesis_options,
            SYNTHESIS_OPTION_FIELDS
        )
    )

    if (hasInvestigation) out.investigation = target

    // Legacy flat files keep flat keys; nothing else to do — `target` IS `out`.

    return out
}
